/**
 * The body of the task detail screen: attributes, timing strip, the input and
 * output JSON (with payload-by-URL handling), and a failure block with a
 * copy-as-bug-report affordance.
 *
 * Was a side panel; the prototype makes task detail a destination, so this is
 * now the screen's content rather than a column beside the list.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { getTaskInput, getTaskOutput } from '@optum/api-client/endpoints/tasks';
import type { Task, TaskLog, TaskPayload } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { KeyValue } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Band } from '../../ui/Band.js';
import { CostPanel, useTaskCost } from '../cost/CostPanel.js';
import { linkageCanvasPath } from '../automation/paths.js';
import {
  ForkedSteps,
  IndependentRun,
  LinkedFrom,
  TaskRuns,
  useTaskLinkage,
} from '../linkages/TaskLinkage.js';
import { ProfilePanel } from '../sandbox/ProfilePanel.js';
import { JsonViewer } from './JsonViewer.js';
import { LogPanel, formatLogText, useTaskLog } from './LogPanel.js';
import { OutcomeWord } from './OutcomeWord.js';
import { TaskDeliveries } from './TaskDeliveries.js';
import { TimingStrip } from './TimingStrip.js';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function isTerminal(task: Task): boolean {
  return TERMINAL_STATUSES.has(task.status);
}

export function TaskDetailBody({ taskId, task }: { taskId: string; task: Task }) {
  const toast = useToast();
  const cost = useTaskCost(taskId, isTerminal(task));
  // Read here as well as in the panel so the bug report can carry it.
  const logs = useTaskLog(taskId, task.status);
  const linkage = useTaskLinkage(taskId);
  // A linked task, a schedule run's included, never started runs of its own.
  const runs = task.linkage_step ? [] : (linkage.data?.runs ?? []);
  const input = useQuery({ queryKey: qk.taskInput(taskId), queryFn: () => getTaskInput(taskId) });
  const output = useQuery({
    queryKey: qk.taskOutput(taskId),
    queryFn: () => getTaskOutput(taskId),
    // Output is meaningful once there is any: terminal for an ordinary
    // task, and from the first snapshot for one that streams.
    enabled: task.status === 'completed' || task.status === 'failed' || task.status === 'streaming',
    // A streaming task's output grows while it runs.
    refetchInterval: task.status === 'streaming' ? 1000 : false,
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Section title="Summary">
        <KeyValue
          dense
          items={[
            {
              label: 'Status',
              value: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11 }}>
                  <StatusBadge status={task.status} vocabulary="task" />
                  <OutcomeWord success={task.success} />
                </span>
              ),
              mono: false,
            },
            { label: 'Source', value: task.source },
            ...(task.linkage_step
              ? [
                  {
                    label: 'Linkage',
                    value: <LinkedFrom task={task} linkage={linkage.data} />,
                    mono: false,
                  },
                ]
              : []),
            // The template the run follows, its canvas a click away (Feature 77 D20).
            ...(task.linkage_template
              ? [
                  {
                    label: 'Template',
                    value: (
                      <Link to={linkageCanvasPath(task.linkage_template)}>
                        {task.linkage_template}
                      </Link>
                    ),
                  },
                ]
              : []),
            // The steps a call forked as it was accepted, each its own task (Feature 78 D17).
            ...(task.forked && task.forked.length > 0
              ? [{ label: 'Forked', value: <ForkedSteps forked={task.forked} />, mono: false }]
              : []),
            {
              label: 'Submitter',
              value: task.submitted_by_external_sub
                ? `${task.submitted_by_idp} · ${task.submitted_by_external_sub}`
                : '—',
            },
            { label: 'Capability', value: task.capability_id },
            { label: 'Agent', value: task.agent_id },
            { label: 'Org', value: task.organization_id },
          ]}
        />
      </Section>

      <Section title="Timing">
        <TimingStrip task={task} />
      </Section>

      {task.failure_reason && (
        <Section title="Failure">
          <div
            style={{
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--danger-line)',
              background: 'var(--danger-tint)',
              padding: 11,
              fontSize: 'var(--text-meta-lg)',
            }}
          >
            <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: 'var(--danger)' }}>
              {task.failure_reason}
            </p>
            <Button
              variant="ghost"
              size="sm"
              icon="content_copy"
              onClick={() => {
                void navigator.clipboard?.writeText(formatBugReport(task, logs.data));
                toast.success('Bug report copied');
              }}
              style={{ marginTop: 7 }}
            >
              Copy as bug report
            </Button>
          </div>
        </Section>
      )}

      {/* A first task's runs are why it is still running, so they sit with
          its timing (Feature 71, D7). */}
      {runs.length > 0 && linkage.data && (
        <Section title="Linkage">
          <TaskRuns task={task} linkage={linkage.data} />
        </Section>
      )}
      {/* An independent task heads its own subtree, which its page draws (D42). */}
      {task.linkage_mode === 'independent' && linkage.data && (
        <Section title="Linkage">
          <IndependentRun task={task} linkage={linkage.data} />
        </Section>
      )}

      {/* The run's own narrative sits beside the failure it explains, before
          the payloads. */}
      <Section title="Logs">
        <LogPanel taskId={taskId} status={task.status} />
      </Section>

      <Section title="Input">
        <PayloadView payload={input.data} />
      </Section>
      <Section title="Output">
        <PayloadView payload={output.data} />
      </Section>

      {/* After Output: what the task produced, then who was told. */}
      <Section title="Deliveries">
        <TaskDeliveries taskId={taskId} />
      </Section>

      {/* Every run is metered; the section follows whether this one recorded
          anything, which the route answers once the run is over. */}
      {cost.data && (
        <Section title="Cost">
          <CostPanel taskId={taskId} />
        </Section>
      )}

      {/* Only an operator-test run is ever profiled, and the endpoint hides
          the profile from anyone else — so the section follows the flag. */}
      {task.profile_requested && isTerminal(task) && (
        <Section title="Profile">
          <ProfilePanel taskId={taskId} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <Band label={title} bare />
      {children}
    </section>
  );
}

function PayloadView({ payload }: { payload: TaskPayload | undefined }) {
  if (!payload) {
    return (
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>Not loaded yet.</p>
    );
  }
  if (payload.inline !== null && payload.inline !== undefined) {
    return <JsonViewer value={payload.inline} />;
  }
  if (payload.url) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--border-hairline)',
          padding: 11,
          fontSize: 'var(--text-body-md)',
        }}
      >
        <p>
          Payload exceeds the inline-size threshold ({fmtBytes(payload.size_bytes)}). Open the
          signed URL to download:
        </p>
        <a
          href={payload.url}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 'var(--text-meta-lg)', textDecoration: 'underline' }}
          className="mono"
        >
          {payload.url}
        </a>
      </div>
    );
  }
  return <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>(none)</p>;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatBugReport(task: Task, log?: TaskLog): string {
  return [
    `Task: ${task.id}`,
    `Capability: ${task.capability_id}`,
    `Agent: ${task.agent_id}`,
    `Org: ${task.organization_id}`,
    `Source: ${task.source}`,
    `Status: ${task.status}`,
    task.submitted_by_external_sub
      ? `Submitter: ${task.submitted_by_idp} · ${task.submitted_by_external_sub}`
      : '',
    `Created: ${task.created_at}`,
    task.started_at ? `Started: ${task.started_at}` : '',
    task.completed_at ? `Completed: ${task.completed_at}` : '',
    '',
    'Failure:',
    task.failure_reason ?? '(none)',
    ...(log ? ['', formatLogText(log)] : []),
  ]
    .filter(Boolean)
    .join('\n');
}
